import React from 'react';
import { motion } from 'framer-motion';
import {
  FaArrowRight,
  FaBrain,
  FaCarSide,
  FaCogs,
  FaDatabase,
  FaMobileAlt,
  FaRocket,
  FaShieldAlt,
  FaStore,
  FaTerminal
} from 'react-icons/fa';
import '../css/Hero.css';

const orbitItems = ['React', 'FastAPI', 'AI', 'MongoDB', 'Mobile', 'CAN'];

const missionCards = [
  { icon: <FaBrain />, label: 'AI Automation', value: 'Assistants, LLM tools, workflows' },
  { icon: <FaMobileAlt />, label: 'Web + Mobile', value: 'Bookings, dashboards, product UI' },
  { icon: <FaCogs />, label: 'BAS Software', value: 'BACnet, Node-RED, Grafana labs' }
];

const projectSignals = [
  { icon: <FaCogs />, title: 'BAS Field + Software', text: 'Low-voltage automation tools' },
  { icon: <FaStore />, title: 'MTB Fabrics', text: 'Fabric business website' },
  { icon: <FaCarSide />, title: 'Mechanic Finders', text: 'Mobile service platform' }
];

export default function Hero() {
  return (
    <section className="hero-section" id="hero">
      <div className="hero-field" />
      <div className="hero-scanline hero-scanline-one" />
      <div className="hero-scanline hero-scanline-two" />
      <div className="hero-light hero-light-one" />
      <div className="hero-light hero-light-two" />

      <div className="hero-shell">
        <motion.div
          className="hero-command-panel"
          initial={{ opacity: 0, y: 38 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="hero-console-top">
            <span className="console-dot" />
            <span className="console-title">ONUR_API / BUILD_SYSTEM</span>
            <span className="console-status">LIVE</span>
          </div>

          <motion.div
            className="hero-eyebrow-row"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.5 }}
          >
            <span><FaRocket /> Full stack product builder</span>
            <span><FaShieldAlt /> Secure backend systems</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 26 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.18, duration: 0.7 }}
          >
            Software engineer for web, mobile, AI automation, and BAS tools.
          </motion.h1>

          <motion.p
            className="hero-lead"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.32, duration: 0.65 }}
          >
            React, React Native, FastAPI, Node.js, Flask, Python, Java,
            MongoDB, BAS field exposure, BACnet learning, and real client product work.
          </motion.p>

          <motion.div
            className="hero-actions"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.46, duration: 0.55 }}
          >
            <a href="#projects" className="hero-btn hero-btn-primary">
              <span>Open Project Deck</span>
              <FaArrowRight />
            </a>
            <a href="#experience" className="hero-btn hero-btn-secondary">
              View Experience OS
            </a>
          </motion.div>

          <motion.div
            className="mission-grid"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.58, duration: 0.6 }}
          >
            {missionCards.map((card) => (
              <motion.div className="mission-card" key={card.label} whileHover={{ y: -5 }}>
                <span className="mission-icon">{card.icon}</span>
                <div>
                  <strong>{card.label}</strong>
                  <small>{card.value}</small>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>

        <motion.div
          className="hero-visual-stage"
          initial={{ opacity: 0, scale: 0.94, x: 36 }}
          animate={{ opacity: 1, scale: 1, x: 0 }}
          transition={{ delay: 0.2, duration: 0.85 }}
        >
          <div className="stage-perspective">
            <div className="stage-floor" />

            <div className="signal-orbit">
              {orbitItems.map((item, index) => (
                <span key={item} className={`orbit-chip orbit-chip-${index + 1}`}>
                  {item}
                </span>
              ))}
            </div>

            <div className="hero-device">
              <div className="device-grid" />
              <div className="device-header">
                <FaTerminal />
                API CONTROL
              </div>
              <div className="device-main">
                <div className="device-core">
                  <FaDatabase />
                  <span>API</span>
                </div>
                <div className="device-path path-one" />
                <div className="device-path path-two" />
                <div className="device-path path-three" />
              </div>
              <div className="device-footer">
                <span>BUILD</span>
                <strong>React / FastAPI / MongoDB</strong>
              </div>
            </div>

            <div className="signal-stack">
              {projectSignals.map((signal, index) => (
                <motion.article
                  className={`signal-card signal-card-${index + 1}`}
                  key={signal.title}
                  animate={{ y: [0, -12, 0] }}
                  transition={{
                    repeat: Infinity,
                    duration: 4.5 + index * 0.5,
                    ease: 'easeInOut'
                  }}
                >
                  <span>{signal.icon}</span>
                  <div>
                    <strong>{signal.title}</strong>
                    <small>{signal.text}</small>
                  </div>
                </motion.article>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
